import { Drawer, Grid, Stack, Typography } from "@mui/material"
import { formatPhone } from 'utils/phone'
import PreviewCard from "components/PreviewCard"
import dayjs from "dayjs"
import FeildSkeleton from "pages/Provider/Employees/Person/FieldSkeleton"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import StudentDetailsForm from "./Form/StudentDetailsForm"
import { useSession } from "components/Providers"
import SiblingInfoPopup from "./SiblingInfoPopup"
import StaffEmploymentInfo from "./StaffEmploymentInfo"
import { getSiblingInfo, getParentEmploymentInfo } from "store/application/student-applications"
import { useDispatch, useSelector } from "react-redux"
import * as snackbar from 'utils/snackbar'
import { HighlightOff, Verified } from "@mui/icons-material"

export default function StudentProfile({ applicationRecord, setApplicationRecord, academicYears }) {
  const { t } = useTranslation()
  const [openDrawer, setOpenDrawer] = useState(false)
  const { district } = useSession()
  const [openSiblingPopup, setOpenSiblingPopup] = useState(false);
  const [selectedSiblings, setSelectedSiblings] = useState(null);
  const [openStaffPopup, setOpenStaffPopup] = useState(false);
  const [staffEmployment, setStaffEmployment] = useState(null)
  const dispatch = useDispatch();
  const siblingChecks = applicationRecord?.applicationSchools?.map(item => item.siblingCheck) ?? []
  const isSibling = siblingChecks.some(v => v === true) ? true
    : siblingChecks.some(v => v === false) ? false
      : null
  const loading = useSelector(state => state.application.studentApplications.instanceLoading)
  const siblingInfo = {
    siblingFirstName: applicationRecord?.siblingFirstName ?? "",
    siblingLastName: applicationRecord?.siblingLastName ?? "",
    siblingGrade: applicationRecord?.siblingGrade ?? ""
  }


  const studentDetails = [
    { key: "Full Name", value: applicationRecord?.firstName && applicationRecord?.lastName ? `${applicationRecord.firstName} ${applicationRecord.lastName}` : "" },
    { key: "Date of Birth", value: applicationRecord?.dateOfBirth ? dayjs(applicationRecord.dateOfBirth).format("MM/DD/YYYY") : "" },
    { key: "Application ID", value: applicationRecord?.id ?? "" },
    { key: "Application Date", value: applicationRecord?.createdAt ? dayjs(applicationRecord.createdAt).format("MM/DD/YYYY") : "" },
    { key: "Grade Applying For", value: applicationRecord?.studentGr?.name ?? "" },
    { key: "Address", value: applicationRecord?.address ? `${applicationRecord.address.street || ""} ${applicationRecord.address.city || ""}, ${applicationRecord.address.state || ""} ${applicationRecord.address.zip || ""}`.trim() : "" },
    { key: "Parent Name", value: applicationRecord?.parentFirstName && applicationRecord?.parentLastName ? `${applicationRecord.parentFirstName} ${applicationRecord.parentLastName}` : "" },
    { key: "Parent Email", value: applicationRecord?.parentEmail ?? "" },
    { key: "Parent Phone", value: applicationRecord?.parentPhone ? formatPhone(applicationRecord.parentPhone) : "" },
    { key: "Economically Disadvantaged", value: typeof applicationRecord?.edcStudent === "boolean" ? (applicationRecord.edcStudent ? "Yes" : "No") : "" },
    { key: "ELL Student", value: typeof applicationRecord?.ellStudent === "boolean" ? (applicationRecord.ellStudent ? "Yes" : "No") : "" },
    {
      key: "Sibling Attending",
      value: typeof applicationRecord?.sibling === "boolean"
        ? (applicationRecord.sibling
          ?
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              component="span"
              color="primary"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={async () => {
                try {
                  await dispatch(getSiblingInfo({ id: applicationRecord.id })).then(res => {
                    setSelectedSiblings(res.payload)
                    setOpenSiblingPopup(true)
                  })
                } catch (error) {
                  snackbar.error(t("Error fetching sibling info."));
                }
              }}
            >
              {t('Yes')}
            </Typography>
            {isSibling === true ? <Verified color="success" fontSize="small" titleAccess={t("Sibling information has been verified")} /> : isSibling === false ? <HighlightOff color="error" fontSize="small" titleAccess={t("Sibling verified but not eligible")} /> : null}
          </Stack>
          : t("No"))
        : ""
    },
    {
      key: "Parent/Guardian Employed",
      value: typeof applicationRecord?.staffPriority === "boolean"
        ? (applicationRecord.staffPriority
          ?
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              component="span"
              color="primary"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={async () => {
                try {
                  await dispatch(getParentEmploymentInfo({ id: applicationRecord.id })).then(res => {
                    setStaffEmployment(res.payload)
                    setOpenStaffPopup(true)
                  })
                } catch (error) {
                  snackbar.error(t("Error fetching employment info."));
                }
              }}
            >
              {t('Yes')}
            </Typography>
            {/* {isSibling === true ? <Verified color="success" fontSize="small" titleAccess={t("Sibling information has been verified")} /> : isSibling === false ? <HighlightOff color="error" fontSize="small" titleAccess={t("Sibling verified but not eligible")} /> : null} */}
          </Stack>
          : "No")
        : ""
    },
  ]

  return (
    <Grid container>
      <Grid item xs={12} md={12} sx={{ height: '100%', mx: 3, mt: 3 }}>
          <PreviewCard
            title={t('Personal Information')}
            size="large"
            {...(district?.isServiceProvider && {
              buttonLabel: t('More'),
              buttonClick: () => {
                setOpenDrawer(true)
              },
              buttonNavigate: true,
            })}
            buttonPermission={'application.application'}
            actionChildren={''}
          >
            <Grid container spacing={2}>
              {studentDetails.map((item, index) => (
                <Grid key={index} item sm={4} xs={4} md={4}>
                  <Typography
                    className="grid-table-title"
                    variant="body2"
                    sx={{ fontSize: '12px' }}
                  >
                    {t(item.key)}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="grid-table-title-value"
                    sx={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
                  >
                    {/* {item.value ? item.value : <FeildSkeleton />} &nbsp; */}
                    {loading ? <FeildSkeleton /> : (item.value || "")} &nbsp;
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </PreviewCard>
      </Grid>
      <Drawer
        open={openDrawer}
        anchor="right"
        PaperProps={{ sx: { width: 800 } }}
        onClose={() => setOpenDrawer(false)}
      >
        <StudentDetailsForm applicationRecord={applicationRecord} setApplicationRecord={setApplicationRecord} setOpenDrawer={setOpenDrawer} academicYears={academicYears} />
      </Drawer>
      <SiblingInfoPopup
        open={openSiblingPopup}
        onClose={() => setOpenSiblingPopup(false)}
        siblings={selectedSiblings}
        siblingInfo={siblingInfo}
      />
      <StaffEmploymentInfo
        open={openStaffPopup}
        onClose={() => setOpenStaffPopup(false)}
        employment={staffEmployment}
      />
    </Grid>
  )
}
